import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useRecording } from '../../contexts/RecordingContext';
import toast from '../../lib/toast';
import { SOAPNoteMetadata } from '../../types';

const Icon = ({ path, className }: { path: React.ReactNode; className?: string }) => (
  <svg viewBox='0 0 24 24' className={className} fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    {path}
  </svg>
);

const MicIcon = (p: { className?: string }) => <Icon {...p} path={<g><rect x='9' y='4' width='6' height='10' rx='3'/><path d='M5 11a7 7 0 0 0 14 0'/><path d='M12 21v-4'/><path d='M8 21h8'/></g>} />;
const PauseIcon = (p: { className?: string }) => <Icon {...p} path={<g><rect x='6' y='5' width='4' height='14' rx='1'/><rect x='14' y='5' width='4' height='14' rx='1'/></g>} />;
const PlayIcon = (p: { className?: string }) => <Icon {...p} path={<path d='M7 5v14l11-7-11-7Z' />} />;
const StopIcon = (p: { className?: string }) => <Icon {...p} path={<g><rect x='6' y='6' width='12' height='12' rx='2'/></g>} />;
const TrashIcon = (p: { className?: string }) => <Icon {...p} path={<g><path d='M4 7h16'/><path d='M9 4h6l1 3H8l1-3Z'/><path d='M10 11v6'/><path d='M14 11v6'/><path d='M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7'/></g>} />;
const RefreshIcon = (p: { className?: string }) => <Icon {...p} path={<g><path d='M21 12a9 9 0 1 1-2.64-6.36L21 8'/><path d='M21 3v5h-5'/></g>} />;

interface ActionRailProps {
  patient: { patient_id: string; first_name: string; last_name: string };
  notes: SOAPNoteMetadata[];
  onRefreshNotes: () => void;
}

export const ActionRail = ({ patient, notes, onRefreshNotes }: ActionRailProps) => {
  const { activeSessionId: _ignored, sessions, actions, utils, visualizerPeaks, visualizerMeta, visualizerRawLevels } = useRecording();
  const silenceCounterRef = useRef(0);
  const [showSilenceHint, setShowSilenceHint] = useState(false);
  const [initializedSessionId, setInitializedSessionId] = useState<string | null>(null);
  const hasNotes = notes && notes.length > 0;
  const sorted = useMemo(() => [...(notes || [])].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()), [notes]);
  const latest = sorted[0] || null;
  const [noteType, setNoteType] = useState(hasNotes ? 'FOLLOWUP' : 'INITIAL');
  const [previousSessionId, setPreviousSessionId] = useState<string | null>(hasNotes ? latest?.session_id : null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (initializedSessionId) return;
    if (hasNotes) {
      setNoteType('FOLLOWUP');
      setPreviousSessionId(latest?.session_id || null);
    } else {
      setNoteType('INITIAL');
      setPreviousSessionId(null);
    }
  }, [hasNotes, latest, initializedSessionId]);

  useEffect(() => {
    if (initializedSessionId) return;
    if (!sessions) return;
    const existing = Object.values(sessions).find((s: any) => s.patientId === patient.patient_id && ['recording', 'paused', 'finishing', 'uploading', 'processing', 'done'].includes(s.status));
    if (existing) setInitializedSessionId((existing as any).session_id);
  }, [sessions, initializedSessionId, patient.patient_id]);

  const currentSession = initializedSessionId ? sessions[initializedSessionId] : null;
  const isRecording = currentSession && currentSession.status === 'recording';
  const isPaused = currentSession && currentSession.status === 'paused';
  const isProcessing = currentSession && ['finishing', 'uploading', 'processing'].includes(currentSession.status);

  const start = async () => {
    if (initializedSessionId) return;
    setStarting(true);
    try {
      const extra = noteType === 'INITIAL' ? { noteType } : { noteType, previousSessionId };
      const sid = await actions.startRecording(patient.patient_id, `${patient.first_name} ${patient.last_name}`, extra);
      if (sid) setInitializedSessionId(sid);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to start recording');
    } finally {
      setStarting(false);
    }
  };

  const pause = () => initializedSessionId && actions.pauseRecording(initializedSessionId);
  const resume = () => initializedSessionId && actions.resumeRecording(initializedSessionId);
  const finishAndReset = async () => {
    if (!initializedSessionId) return;
    try { await actions.stopRecording(initializedSessionId); }
    finally { setInitializedSessionId(null); }
  };
  const discard = async () => {
    if (!initializedSessionId) return;
    await actions.discardSession(initializedSessionId);
    setInitializedSessionId(null);
  };

  const durationMs = currentSession ? utils.computeDurationMs(currentSession) : 0;
  const sizeStr = currentSession ? utils.formatBytes(currentSession.totalBytes) : '0 B';

  useEffect(() => {
    if (initializedSessionId && !currentSession) setInitializedSessionId(null);
  }, [initializedSessionId, currentSession]);

  useEffect(() => {
    if (!(initializedSessionId && (isRecording || isPaused) && visualizerRawLevels)) {
      silenceCounterRef.current = 0; setShowSilenceHint(false); return;
    }
    if (isRecording && visualizerMeta) {
      if (!visualizerMeta.speaking) {
        silenceCounterRef.current++;
        if (silenceCounterRef.current > 150) setShowSilenceHint(true);
      } else {
        silenceCounterRef.current = 0; setShowSilenceHint(false);
      }
    }
  }, [visualizerRawLevels, initializedSessionId, isRecording, isPaused, visualizerMeta]);

  const previousNote = useMemo(() => {
    if (!previousSessionId) return null;
    return (notes || []).find(n => n.session_id === previousSessionId) || null;
  }, [previousSessionId, notes]);

  const previousNoteDateStr = useMemo(() => {
    if (!previousNote?.created_at) return null;
    try {
      return new Date(previousNote.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  }, [previousNote]);

  const showIdleStart = !initializedSessionId;

  return (
    <div className='space-y-4'>
      {showIdleStart && (
        <div className='bg-white rounded-lg border border-brand-navy/15 shadow-sm p-4 space-y-4'>
          <h3 className='text-sm font-semibold text-brand-navy'>Start a Note</h3>
          <div className='relative'>
            <div className='relative rounded-lg border border-brand-navy/15 bg-white p-1'>
              <div
                className='absolute top-1 bottom-1 rounded-md bg-brand-navy shadow-sm transition-all duration-300 ease-out pointer-events-none'
                style={{ left: noteType === 'INITIAL' ? '0.25rem' : 'calc(50% + 0.25rem)', width: 'calc(50% - 0.5rem)' }}
              />
              <div className='grid grid-cols-2 text-xs font-medium relative z-10 select-none'>
                <button type='button' aria-pressed={noteType === 'INITIAL'} disabled={starting}
                  onClick={() => { setNoteType('INITIAL'); setPreviousSessionId(null); }}
                  className={`py-2.5 text-center transition-colors ${noteType === 'INITIAL' ? 'text-white' : 'text-gray-600 hover:text-brand-navy'}`}
                >Initial Note</button>
                <button type='button' aria-pressed={noteType === 'FOLLOWUP'} disabled={!hasNotes || starting}
                  onClick={() => { setNoteType('FOLLOWUP'); if (!previousSessionId) setPreviousSessionId(latest?.session_id || null); }}
                  className={`py-2.5 text-center transition-colors ${noteType === 'FOLLOWUP' ? 'text-white' : 'text-gray-600 hover:text-brand-navy'} ${!hasNotes ? 'cursor-not-allowed opacity-40' : ''}`}
                >Follow-up</button>
              </div>
            </div>
          </div>
          <div className='text-[11px] text-gray-500 min-h-[16px]'>
            {noteType === 'FOLLOWUP'
              ? previousNoteDateStr ? <>Based on session from <span className='font-medium'>{previousNoteDateStr}</span></> : <>Based on previous session</>
              : !hasNotes && 'First note for this patient.'}
          </div>
          <button onClick={start} disabled={starting || (noteType === 'FOLLOWUP' && !previousSessionId)}
            className='w-full relative overflow-hidden rounded-lg bg-brand-navy text-white py-3 flex items-center justify-center gap-2 text-sm font-medium shadow hover:bg-brand-navy/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-accent'>
            {starting
              ? <span className='flex items-center gap-2'><span className='w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin' /> Initializing…</span>
              : <><MicIcon className='w-5 h-5' />{noteType === 'INITIAL' ? 'Start Initial Recording' : 'Start Follow-up Recording'}</>}
          </button>
        </div>
      )}
      {initializedSessionId && (isRecording || isPaused) && (
        <div className='bg-white rounded-lg border border-brand-navy/15 shadow-sm p-4 space-y-3'>
          <div className='flex items-center justify-between'>
            <h3 className='text-sm font-semibold text-brand-navy'>Recording</h3>
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-2'>
                <span className={`w-2.5 h-2.5 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'} shadow-inner`} />
                <span className='font-mono text-lg tabular-nums tracking-tight text-gray-900'>{utils.formatDuration(durationMs)}</span>
              </div>
              <span className='text-[10px] font-mono text-gray-500'>{sizeStr}</span>
            </div>
          </div>
          <div className='h-20 -mx-1 flex items-end gap-[4px] select-none relative rounded px-2 overflow-hidden bg-gradient-to-b from-white via-brand-navy/5 to-brand-navy/10'>
            {(visualizerRawLevels || new Array(32).fill(0)).map((raw: number, i: number) => {
              let v = Math.min(1, raw * (visualizerMeta?.speaking ? 2.2 : 1.9));
              if (showSilenceHint && isRecording && !visualizerMeta?.speaking) {
                const t = Date.now() / 1000;
                v = 0.08 + (Math.sin(t * 1.1 + i * 0.25) + 1) / 2 * 0.16;
              }
              const peak = visualizerPeaks ? Math.min(1, visualizerPeaks[i] * (visualizerMeta?.speaking ? 2.2 : 1.9)) : v;
              const barHeight = Math.max(3, v * 100);
              const peakTop = Math.max(2, peak * 100);
              const alpha = isPaused ? 0.35 : Math.min(0.95, 0.25 + v * 0.6);
              return (
                <div key={i} className='flex-1 h-full relative'>
                  <span className='absolute bottom-0 left-0 right-0 rounded-sm transition-[height,background] duration-90 ease-linear origin-bottom'
                    style={{ height: `${barHeight}%`, opacity: isPaused ? 0.55 : 1, background: `rgba(8,32,62,${alpha})` }} />
                  <span className='absolute left-0 right-0 h-0.5 bg-white/70 rounded-sm'
                    style={{ bottom: `${peakTop}%`, opacity: isPaused ? 0.15 : 0.55 }} />
                </div>
              );
            })}
            {showSilenceHint && isRecording && !visualizerMeta?.speaking && (
              <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
                <span className='text-[10px] px-2 py-1 rounded bg-white/70 text-brand-navy font-medium backdrop-blur-sm'>Listening… (low input)</span>
              </div>
            )}
          </div>
          <div className='flex items-center gap-3'>
            {isRecording && <button onClick={pause} title='Pause' className='h-10 w-10 rounded-full bg-yellow-500 text-white flex items-center justify-center hover:bg-yellow-600 shadow focus:outline-none focus:ring-2 focus:ring-brand-accent'><PauseIcon className='w-5 h-5' /></button>}
            {isPaused && <button onClick={resume} title='Resume' className='h-10 w-10 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 shadow focus:outline-none focus:ring-2 focus:ring-brand-accent'><PlayIcon className='w-5 h-5' /></button>}
            <button onClick={finishAndReset} title='Stop & Process' className='h-10 w-10 rounded-full bg-brand-navy text-white flex items-center justify-center hover:bg-brand-navy/90 shadow focus:outline-none focus:ring-2 focus:ring-brand-accent'><StopIcon className='w-5 h-5' /></button>
            <button onClick={discard} title='Discard' className='h-10 w-10 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-300 shadow focus:outline-none focus:ring-2 focus:ring-brand-accent'><TrashIcon className='w-5 h-5' /></button>
          </div>
          <div className='flex items-center justify-between text-[11px] text-gray-500'>
            <span className='capitalize'>State: {currentSession?.status}</span>
            <span className='flex items-center gap-1 cursor-pointer text-brand-navy' title='Refresh Notes' onClick={onRefreshNotes}>
              <RefreshIcon className='w-4 h-4' />
            </span>
          </div>
        </div>
      )}
      {initializedSessionId && isProcessing && (
        <div className='bg-white rounded-lg border border-brand-navy/15 shadow-sm p-4 space-y-2'>
          <h3 className='text-sm font-semibold text-brand-navy'>Processing</h3>
          <p className='text-xs text-gray-600'>Transcribing & generating note…</p>
          <div className='flex items-center gap-2'>
            <span className='w-2 h-2 rounded-full bg-brand-accent animate-pulse' />
            <span className='text-[11px] text-gray-500'>Please wait</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionRail;
